import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaFutbol, FaTrophy, FaUsers, FaPlay } from 'react-icons/fa';
import { achievements } from './data/achievements';
import founderImage from './images/about-the-founder.jpg';
import MatchCard from './components/match/MatchCard';
// import clarityService from './services/clarityService'; // Disabled - tracking removed for App Store compliance
import './scss/academyWelcomePage.scss';

function AcademyWelcomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [clickedAchievement, setClickedAchievement] = useState(null);
  
  // Get team ID from URL params or default to 74
  const searchParams = new URLSearchParams(location.search);
  const teamId = searchParams.get('team_id') || '74';
  const teamName = searchParams.get('team_name') || 'Redhill Rovers Academy';
  
  // Academy details - in a real app, this would come from an API
  const academyName = teamName;
  const academyLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-dark.png`;
  
  // Add body class for this page
  useEffect(() => {
    document.body.classList.add('academy-welcome-page');
    
    // Clarity tracking disabled - removed for App Store compliance
    // clarityService.trackEvent('academy_welcome_page_view', {
    //   teamId: teamId,
    //   teamName: academyName
    // });
    
    return () => {
      document.body.classList.remove('academy-welcome-page');
    };
  }, [teamId, academyName, academyLogo]);
  
  // Sample match data for preview - using the team name from URL
  const sampleMatches = [
    {
      match_id: 'demo-future',
      match_date: '2024-12-15',
      match_time: '14:00:00',
      location: 'Home Pitch',
      opposition: 'Local Rivals',
      entry_open: 1,
      max_players: 12,
      total_yes: 8,
      total_no: 2,
      total_awaiting: 2,
      active_yes: 8,
      waitlist_yes: 0,
      is_home: 1,
      match_type: 'friendly',
      response: null,
      team_name: academyName // Use the actual team name from URL
    },
    {
      match_id: 'demo-past',
      match_date: '2024-12-01',
      match_time: '14:00:00',
      location: 'Away Ground',
      opposition: 'City United',
      entry_open: null,
      max_players: 12,
      total_yes: 10,
      total_no: 2,
      total_awaiting: 0,
      active_yes: 10,
      waitlist_yes: 0,
      is_home: 0,
      match_type: 'friendly',
      response: 'Yes',
      score_white: 3,
      score_colour: 2,
      motm: 'demo-player',
      team_name: academyName, // Use the actual team name from URL
      scorers: {
        'White': [
          { name: 'Charlie B', goals: 2 },
          { name: 'Alex K', goals: 1 }
        ],
        'Colour': [
          { name: 'Tom M', goals: 1 },
          { name: 'Sam P', goals: 1 }
        ]
      }
    },
    {
      match_id: 'demo-future-2',
      match_date: '2024-12-22',
      match_time: '15:30:00',
      location: 'Community Ground',
      opposition: 'Youth Academy',
      entry_open: 1,
      max_players: 12,
      total_yes: 6,
      total_no: 1,
      total_awaiting: 5,
      active_yes: 6,
      waitlist_yes: 0,
      is_home: 0,
      match_type: 'friendly',
      response: null,
      team_name: academyName // Use the actual team name from URL
    }
  ];
  
  // Achievement data for preview
  const previewAchievements = [
    { id: 'iron_man', isAchieved: true, isLatest: true },
    { id: 'hat_trick', isAchieved: true, isLatest: false },
    { id: 'mvp', isAchieved: false, isLatest: false },
    { id: 'hot_streak', isAchieved: false, isLatest: false },
    { id: 'first_strike', isAchieved: true, isLatest: false },
    { id: 'star_performer', isAchieved: false, isLatest: false }
  ];
  
  const handleGetStarted = () => {
    setIsLoading(true);
    
    // Clarity tracking disabled - removed for App Store compliance
    // clarityService.trackEvent('academy_welcome_cta_click', {
    //   teamId: teamId,
    //   teamName: academyName,
    //   buttonText: 'Ready to Start?'
    // });
    
    // Navigate to team-specific HTML page (for iOS home screen compatibility)
    setTimeout(() => {
      window.location.href = `/${teamId}.html#v`;
    }, 300);
  };

  const handleAchievementClick = (achievementId) => {
    setClickedAchievement(achievementId);
    
    // Clarity tracking disabled - removed for App Store compliance
    // const achievementData = achievements.find(a => a.id === achievementId);
    // clarityService.trackEvent('achievement_click', {
    //   achievementId: achievementId,
    //   achievementTitle: achievementData?.title || 'Unknown',
    //   teamId: teamId,
    //   teamName: academyName
    // });
    
    // Reset after animation
    setTimeout(() => {
      setClickedAchievement(null);
    }, 1000);
  };

  return (
    <div className="academy-welcome-page">
      <Helmet>
        <title>{`${academyName} - FirstWhistle Academy Welcome`}</title>
        <meta name="description" content={`Welcome to ${academyName}! Join FirstWhistle to track player development, achievements, and keep families connected.`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`${academyName} - FirstWhistle Academy Welcome`} />
        <meta property="og:description" content={`Welcome to ${academyName}! Join FirstWhistle to track player development, achievements, and keep families connected.`} />
        <meta property="og:image" content={academyLogo} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={`${academyName} - FirstWhistle Academy Welcome`} />
        <meta name="twitter:description" content={`Welcome to ${academyName}! Join FirstWhistle to track player development, achievements, and keep families connected.`} />
        <meta name="twitter:image" content={academyLogo} />
        
        {/* Additional meta tags for better SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="FirstWhistle" />
        <meta name="keywords" content={`${academyName}, football academy, player development, achievements, FirstWhistle`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <div className="welcome-container">
        {/* Header with Academy Logo */}
        <div className="academy-header">
          <div className="logo-container">
            <img 
              src={academyLogo} 
              alt={`${academyName} logo`} 
              className="academy-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="logo-placeholder" style={{ display: 'none' }}>
              <FaFutbol size={60} />
            </div>
          </div>
          <h1 className="academy-name">{academyName}</h1>
        </div>

        {/* FOMO Section */}
        <div className="fomo-section">
          <div className="fomo-badge">
            <span className="fomo-icon">🎉</span>
            <span className="fomo-text">Your team has been added!</span>
          </div>
          
          {/* Personal Message from Dean */}
          <div className="dean-message">
            <div className="dean-avatar">
              <img src={founderImage} alt="Dean - FirstWhistle Founder" />
            </div>
            <div className="dean-content">
              <h4>Hi, I'm Dean</h4>
              <p>As a parent and football organiser, I saw the need for better communication and player recognition. That's why I created FirstWhistle and I'd love to help your academy.</p>
            </div>
          </div>
          
          <h2 className="fomo-title">You're 1 click away from your best season</h2>
          <p className="fomo-subtitle">Join coaches already using FirstWhistle to boost player development and family engagement</p>
        </div>

        {/* Benefits Grid - Reduced to 2 */}
        <div className="benefits-section">
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaTrophy />
              </div>
              <h3>Achievement Tracking</h3>
                              <p>Celebrate player milestones with badges, POTM awards, and progress tracking</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaUsers />
              </div>
              <h3>Family<br />Engagement</h3>
              <p>Keep parents informed with match reports, photos, and more!</p>
            </div>
          </div>
        </div>

        {/* Achievement Preview */}
        <div className="achievement-preview-section">
          <h3 className="preview-title">See Achievements in Action</h3>
          <div className="achievement-grid">
            {previewAchievements.map((previewAchievement) => {
              const achievementData = achievements.find(a => a.id === previewAchievement.id);
              if (!achievementData) return null;
              
              return (
                <div 
                  key={previewAchievement.id}
                  className={`achievement-coin ${previewAchievement.isAchieved ? 'achieved' : 'locked'} ${clickedAchievement === previewAchievement.id ? 'clicked' : ''}`}
                  onClick={() => handleAchievementClick(previewAchievement.id)}
                >
                  {previewAchievement.isLatest && (
                    <div className="latest-badge">Latest!</div>
                  )}
                  <div className="coin-content">
                    <div className="award-icon">
                      <div className={`award-card ${previewAchievement.isAchieved ? 'achieved' : 'locked'}`}>
                        {achievementData.icon}
                      </div>
                    </div>
                    <div className="award-info">
                      <h3>{achievementData.title}</h3>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="achievement-description">
            This is just an example. Players earn many more achievement coins including Goal Machine, Playmaker, Defensive Rock, and special awards given at match reports.
          </p>
        </div>

        {/* Social Proof - Moved above setup steps */}
        <div className="social-proof-section">
          <div className="social-proof-card">
            <div className="quote-icon">"</div>
            <p className="testimonial">
              "I love seeing my achievements and match reports! It makes me want to work harder in every game."
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">L</div>
              <div className="author-info">
                <h4>Leland, Age 12</h4>
                <span>Player</span>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Steps */}
        <div className="setup-steps-section">
          <h3 className="setup-title">Getting Started is Easy</h3>
          <div className="setup-steps">
            <div className="setup-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Click Ready to Start</h4>
                <p>Your team profile is ready to go - use mobile number and password which has been sent to you in a message or email</p>
              </div>
            </div>
            <div className="setup-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Add Players</h4>
                <p>Quickly add your team roster with player details</p>
              </div>
            </div>
            <div className="setup-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Invite Parents / Guardians</h4>
                <p>Connect families to keep them informed and engaged</p>
              </div>
            </div>
          </div>
          <p className="setup-description">
            You are then ready to add matches, training sessions, match reports, player achievements and more!
          </p>
        </div>

        {/* CTA Button */}
        <div className="cta-section">
          <button 
            className={`cta-button ${isLoading ? 'loading' : ''}`}
            onClick={handleGetStarted}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-text">Loading...</span>
            ) : (
              <>
                <span>Ready to Start?</span>
                <FaPlay className="play-icon" />
              </>
            )}
          </button>
          <p className="cta-subtitle">It's FREE forever for coaches</p>
        </div>

        {/* Contact Details */}
        <div className="contact-section">
          <h3>Questions?</h3>
          <p>Get in touch with Dean for any questions about FirstWhistle</p>
          <div className="contact-details">
            <div className="contact-item">
              <span className="contact-label">Email:</span>
              <a href="mailto:dean.hodges@firstwhistle.app" className="contact-link">dean.hodges@firstwhistle.app</a>
            </div>
            <div className="contact-item">
              <span className="contact-label">Phone:</span>
              <a href="tel:07875930733" className="contact-link">07875 930733</a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="welcome-footer">
          <p>Powered by FirstWhistle</p>
        </footer>
      </div>
    </div>
  );
}

export default AcademyWelcomePage; 